import { createContext, useState, useEffect, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { getAuthInstance } from './firebase-init';
import { getClaims } from './auth-methods';
import * as authMethods from './auth-methods';
import type { AuthContextValue, AuthState, AuthUser } from './types';

export const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    claims: null,
    isLoading: true,
    error: null,
  });

  // Subscribe to auth state changes
  useEffect(() => {
    const auth = getAuthInstance();

    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const claims = await getClaims(firebaseUser);
            setState({
              user: toAuthUser(firebaseUser),
              claims,
              isLoading: false,
              error: null,
            });
          } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'An error occurred';
            setState({
              user: toAuthUser(firebaseUser),
              claims: null,
              isLoading: false,
              error: message,
            });
          }
        } else {
          setState({
            user: null,
            claims: null,
            isLoading: false,
            error: null,
          });
        }
      },
      (error) => {
        setState({
          user: null,
          claims: null,
          isLoading: false,
          error: error.message,
        });
      },
    );

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await authMethods.signInWithEmailPassword(email, password);
      // onAuthStateChanged will update state
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw error;
    }
  };

  const signOutHandler = async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await authMethods.signOut();
      // onAuthStateChanged will update state
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw error;
    }
  };

  const refreshClaims = async () => {
    const auth = getAuthInstance();
    const user = auth.currentUser;
    if (user) {
      const claims = await getClaims(user);
      setState((prev) => ({ ...prev, claims }));
    }
  };

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut: signOutHandler,
    getIdToken: authMethods.getIdToken,
    refreshClaims,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
